import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, FileText, Download, Eye, Trash2, Edit, Percent, Search, Filter, Calendar, ArrowRight, CheckCircle, Save } from 'lucide-react';
import { estimates as estimatesAPI, clients as clientsAPI, productsServices } from '../api';
import { useToast } from '../hooks/use-toast';
import { formatMoney } from '../utils/currency';

const Estimates = ({ businessId, businessName, businessCurrency = 'USD' }) => {
  const fmt = (amt) => formatMoney(amt, businessCurrency);
  const { toast } = useToast();
  const [estimates, setEstimates] = useState([]);
  const [clients, setClients] = useState([]);
  const [productsCatalog, setProductsCatalog] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [showItemDropdown, setShowItemDropdown] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingEstimate, setEditingEstimate] = useState(null);
  const [viewEstimate, setViewEstimate] = useState(null);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('valid_until'); // valid_until | created_at | amount | status
  
  // Form state
  const [formData, setFormData] = useState({
    client_name: '',
    client_address: '',
    valid_until: '',
    status: 'draft'
  });
  const [lineItems, setLineItems] = useState([
    { code: '', description: '', unit: 1, unit_price: 0, total: 0 }
  ]);
  const [taxRate, setTaxRate] = useState(0);
  const [discountType, setDiscountType] = useState('none');
  const [discountValue, setDiscountValue] = useState(0);
  
  // Client autocomplete
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [filteredClients, setFilteredClients] = useState([]);

  // Filter estimates
  const filteredEstimates = estimates.filter(estimate => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      estimate.client_name?.toLowerCase().includes(searchLower) ||
      estimate.estimate_number?.toLowerCase().includes(searchLower);
    const matchesStatus = statusFilter === 'all' || estimate.status === statusFilter;
    return matchesSearch && matchesStatus;
  }).sort((a, b) => {
    if (sortBy === 'amount') return (b.amount || 0) - (a.amount || 0);
    if (sortBy === 'status') return (a.status || '').localeCompare(b.status || '');
    if (sortBy === 'created_at') return (b.created_at || '').localeCompare(a.created_at || '');
    // default: valid_until desc, with created_at tiebreaker
    const cmp = (b.valid_until || '').localeCompare(a.valid_until || '');
    if (cmp !== 0) return cmp;
    return (b.created_at || '').localeCompare(a.created_at || '');
  });

  useEffect(() => {
    fetchEstimates();
    fetchClients();
    fetchProductsCatalog();
  }, [businessId]);

  const fetchEstimates = async () => {
    try {
      const response = await estimatesAPI.getAll(businessId);
      setEstimates(response.data || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load estimates',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await clientsAPI.getAll(businessId);
      setClients(response.data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchProductsCatalog = async () => {
    try {
      const response = await productsServices.getAll(businessId);
      setProductsCatalog(response.data || []);
    } catch (error) {
      console.error('Error fetching products catalog:', error);
    }
  };

  const handleSelectProduct = (index, product) => {
    const newItems = [...lineItems];
    const itemCode = product.sku || product.name;
    const itemDescription = product.description || product.name;
    const itemPrice = parseFloat(product.default_price || 0);
    
    newItems[index] = {
      ...newItems[index],
      code: itemCode,
      description: itemDescription,
      unit_price: itemPrice,
      total: newItems[index].unit * itemPrice
    };
    setLineItems(newItems);
    setShowItemDropdown(null);
  };

  const handleClientSearch = (value) => {
    setFormData({ ...formData, client_name: value });
    if (value.length > 0) {
      const filtered = clients.filter(c => 
        c.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredClients(filtered);
      setShowClientDropdown(filtered.length > 0);
    } else {
      setShowClientDropdown(false);
    }
  };

  const selectClient = (client) => {
    setFormData({ 
      ...formData, 
      client_name: client.name,
      client_address: client.address || ''
    });
    setShowClientDropdown(false);
  };

  const openProductDropdownFromField = (index, e) => {
    const rect = e.target.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 2, left: rect.left });
    if (productsCatalog.length > 0) {
      setFilteredItems(productsCatalog);
      setShowItemDropdown(index);
    }
  };

  const handleItemDescriptionChange = (index, value) => {
    const newItems = [...lineItems];
    newItems[index].description = value;
    setLineItems(newItems);

    const text = (value || '').toLowerCase();
    if (text) {
      const filtered = (productsCatalog || []).filter(p =>
        (p.name || '').toLowerCase().includes(text) ||
        (p.sku || '').toLowerCase().includes(text) ||
        (p.description || '').toLowerCase().includes(text)
      );
      setFilteredItems(filtered);
      setShowItemDropdown(filtered.length > 0 ? index : null);
    } else {
      setFilteredItems(productsCatalog || []);
      setShowItemDropdown((productsCatalog || []).length > 0 ? index : null);
    }
  };

  const handleLineItemChange = (index, field, value) => {
    const newItems = [...lineItems];
    newItems[index][field] = field === 'unit' || field === 'unit_price' ? parseFloat(value) || 0 : value;
    newItems[index].total = newItems[index].unit * newItems[index].unit_price;
    setLineItems(newItems);

    // If editing the code field, refilter the product dropdown
    if (field === 'code') {
      const text = (value || '').toLowerCase();
      if (text) {
        const filtered = (productsCatalog || []).filter(p =>
          (p.name || '').toLowerCase().includes(text) ||
          (p.sku || '').toLowerCase().includes(text) ||
          (p.description || '').toLowerCase().includes(text)
        );
        setFilteredItems(filtered);
        setShowItemDropdown(filtered.length > 0 ? index : null);
      } else {
        setFilteredItems(productsCatalog || []);
        setShowItemDropdown((productsCatalog || []).length > 0 ? index : null);
      }
    }
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { code: '', description: '', unit: 1, unit_price: 0, total: 0 }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const calculateTotals = () => {
    const subtotal = lineItems.reduce((sum, item) => sum + item.total, 0);
    let discountAmount = 0;
    if (discountType === 'percentage') {
      discountAmount = subtotal * (discountValue / 100);
    } else if (discountType === 'fixed') {
      discountAmount = discountValue;
    }
    const afterDiscount = subtotal - discountAmount;
    const taxAmount = afterDiscount * (taxRate / 100);
    const total = afterDiscount + taxAmount;
    return { subtotal, discountAmount, taxAmount, total };
  };

  const handleCreateEstimate = async (e) => {
    e.preventDefault();
    const totals = calculateTotals();
    
    const itemsDescription = JSON.stringify({
      items: lineItems,
      subtotal: totals.subtotal,
      tax_rate: taxRate,
      tax_amount: totals.taxAmount,
      discount_type: discountType,
      discount_value: discountValue,
      discount_amount: totals.discountAmount,
      grand_total: totals.total
    });

    try {
      await estimatesAPI.create({
        business_id: businessId,
        ...formData,
        amount: totals.total,
        line_items: lineItems,
        discount_type: discountType,
        discount_value: discountValue,
        discount_amount: totals.discountAmount,
        tax_rate: taxRate,
        tax_amount: totals.taxAmount,
        subtotal: totals.subtotal,
        description: itemsDescription
      });
      
      toast({
        title: 'Success',
        description: 'Estimate created successfully'
      });
      
      setIsDialogOpen(false);
      resetForm();
      fetchEstimates();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create estimate',
        variant: 'destructive'
      });
    }
  };

  const handleEditEstimate = (estimate) => {
    setEditingEstimate(estimate);
    setFormData({
      client_name: estimate.client_name || '',
      client_address: estimate.client_address || '',
      valid_until: estimate.valid_until || '',
      status: estimate.status || 'draft'
    });
    
    // Parse line items
    let items = estimate.line_items || [];
    if (items.length === 0 && estimate.description) {
      try {
        const parsed = JSON.parse(estimate.description);
        items = parsed.items || [];
      } catch (e) {}
    }
    if (items.length === 0) {
      items = [{ code: '', description: '', unit: 1, unit_price: 0, total: 0 }];
    }
    
    setLineItems(items);
    setTaxRate(estimate.tax_rate || 0);
    setDiscountType(estimate.discount_type || 'none');
    setDiscountValue(estimate.discount_value || 0);
    setIsEditDialogOpen(true);
  };

  const handleUpdateEstimate = async (e) => {
    e.preventDefault();
    if (!editingEstimate) return;
    
    const totals = calculateTotals();
    
    try {
      await estimatesAPI.update(editingEstimate.id, {
        ...formData,
        line_items: lineItems,
        subtotal: totals.subtotal,
        discount_type: discountType,
        discount_value: discountValue,
        discount_amount: totals.discountAmount,
        tax_rate: taxRate,
        tax_amount: totals.taxAmount,
        amount: totals.total
      });
      
      toast({
        title: 'Success',
        description: 'Estimate updated successfully'
      });
      
      setIsEditDialogOpen(false);
      setEditingEstimate(null);
      resetForm();
      fetchEstimates();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update estimate',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteEstimate = async (id) => {
    if (!window.confirm('Are you sure you want to delete this estimate?')) return;
    
    try {
      await estimatesAPI.delete(id);
      toast({
        title: 'Success',
        description: 'Estimate deleted successfully'
      });
      fetchEstimates();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete estimate',
        variant: 'destructive'
      });
    }
  };

  const handleConvertToInvoice = async (estimateId) => {
    if (!window.confirm('Convert this estimate to an invoice? This action cannot be undone.')) return;
    
    try {
      const response = await estimatesAPI.convertToInvoice(estimateId);
      const newInvoiceNumber = response?.data?.invoice?.invoice_number || '';
      toast({
        title: 'Success',
        description: `Converted to invoice ${newInvoiceNumber}. Switch to the Invoices tab to view it.`,
      });
      setViewEstimate(null);
      fetchEstimates();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to convert estimate',
        variant: 'destructive'
      });
    }
  };

  const handleExportEstimate = async (estimate, format) => {
    try {
      const response = await estimatesAPI.export(estimate.id, format);
      const blob = new Blob([response.data], { 
        type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `estimate_${estimate.estimate_number}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to export estimate',
        variant: 'destructive'
      });
    }
  };

  const resetForm = () => {
    setFormData({
      client_name: '',
      client_address: '',
      valid_until: '',
      status: 'draft'
    });
    setLineItems([{ code: '', description: '', unit: 1, unit_price: 0, total: 0 }]);
    setTaxRate(0);
    setDiscountType('none');
    setDiscountValue(0);
  };

  const parseEstimateItems = (estimate) => {
    try {
      if (estimate.line_items && estimate.line_items.length > 0) return estimate.line_items;
      if (estimate.description) {
        const parsed = JSON.parse(estimate.description);
        return parsed.items || [];
      }
    } catch (e) {}
    return [];
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800 border-gray-300',
      sent: 'bg-blue-100 text-blue-800 border-blue-300',
      accepted: 'bg-green-100 text-green-800 border-green-300',
      declined: 'bg-red-100 text-red-800 border-red-300',
      converted: 'bg-purple-100 text-purple-800 border-purple-300'
    };
    return colors[status] || colors.draft;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-lg">
        <CardHeader className="border-b bg-gradient-to-r from-green-50 to-emerald-50">
          <div className="flex justify-between items-center">
            <CardTitle className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="h-6 w-6 text-green-600" />
              Estimates
            </CardTitle>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-green-600 hover:bg-green-700 gap-2" data-testid="create-estimate-btn">
                  <Plus className="h-4 w-4" />
                  Create Estimate
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create New Estimate</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateEstimate} className="space-y-6">
                  {/* Client Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative">
                      <Label htmlFor="client_name">Client Name *</Label>
                      <Input
                        id="client_name"
                        value={formData.client_name}
                        onChange={(e) => handleClientSearch(e.target.value)}
                        onFocus={() => formData.client_name && handleClientSearch(formData.client_name)}
                        onBlur={() => setTimeout(() => setShowClientDropdown(false), 200)}
                        required
                        placeholder="Enter client name"
                        autoComplete="off"
                      />
                      {showClientDropdown && (
                        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-40 overflow-y-auto">
                          {filteredClients.map((client) => (
                            <div
                              key={client.id}
                              className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                              onMouseDown={() => selectClient(client)}
                            >
                              <p className="font-medium">{client.name}</p>
                              {client.email && <p className="text-sm text-gray-500">{client.email}</p>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="valid_until">Valid Until *</Label>
                      <Input
                        id="valid_until"
                        type="date"
                        value={formData.valid_until}
                        onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="client_address">Client Address</Label>
                    <Textarea
                      id="client_address"
                      value={formData.client_address}
                      onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
                      rows={2}
                    />
                  </div>

                  {/* Line Items */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <Label className="text-base font-semibold">Line Items</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addLineItem} className="gap-1">
                        <Plus className="h-4 w-4" /> Add Item
                      </Button>
                    </div>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-green-100">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-semibold">Code</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold">Description</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold w-20">Qty</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold w-28">Unit Price</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold w-24">Total</th>
                            <th className="px-3 py-2 w-12"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {lineItems.map((item, index) => (
                            <tr key={index} className="border-b">
                              <td className="p-2">
                                <Input
                                  value={item.code}
                                  onChange={(e) => handleLineItemChange(index, 'code', e.target.value)}
                                  onFocus={(e) => {
                                    const rect = e.target.getBoundingClientRect();
                                    setDropdownPos({ top: rect.bottom + 2, left: rect.left });
                                    if (productsCatalog.length > 0) {
                                      setFilteredItems(productsCatalog);
                                      setShowItemDropdown(index);
                                    }
                                  }}
                                  onBlur={() => setTimeout(() => setShowItemDropdown(null), 300)}
                                  placeholder="SKU / Product"
                                  className="h-9"
                                  autoComplete="off"
                                  data-testid={`estimate-line-item-code-${index}`}
                                />
                                {showItemDropdown === index && filteredItems.length > 0 && ReactDOM.createPortal(
                                  <div
                                    style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, zIndex: 99999, pointerEvents: 'auto' }}
                                    className="w-72 bg-white border border-gray-300 rounded-md shadow-xl max-h-60 overflow-y-auto"
                                    data-testid="estimate-product-dropdown"
                                  >
                                    <div className="px-3 py-1 bg-blue-50 text-xs font-semibold text-blue-700 sticky top-0">
                                      Products & Services
                                    </div>
                                    {filteredItems.map((product) => (
                                      <div
                                        key={product.id}
                                        className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                                        onMouseDown={(e) => { e.preventDefault(); handleSelectProduct(index, product); }}
                                        data-testid={`estimate-product-option-${product.id}`}
                                      >
                                        <div className="flex justify-between">
                                          <span className="font-medium text-sm">{product.name}</span>
                                          <span className="text-sm text-green-600">{formatMoney(product.default_price || 0, businessCurrency)}</span>
                                        </div>
                                        <div className="text-xs text-gray-500">{product.sku || ''}</div>
                                        {product.description && (
                                          <div className="text-xs text-gray-400 truncate">{product.description}</div>
                                        )}
                                      </div>
                                    ))}
                                  </div>,
                                  document.body
                                )}
                              </td>
                              <td className="p-2">
                                <Textarea
                                  value={item.description}
                                  onChange={(e) => handleItemDescriptionChange(index, e.target.value)}
                                  onFocus={(e) => openProductDropdownFromField(index, e)}
                                  onBlur={() => setTimeout(() => setShowItemDropdown(null), 300)}
                                  placeholder="Item description (or pick from products)"
                                  className="min-h-[36px] h-auto resize-none text-sm"
                                  rows={1}
                                  data-testid={`estimate-line-item-description-${index}`}
                                />
                              </td>
                              <td className="p-2">
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={item.unit}
                                  onChange={(e) => handleLineItemChange(index, 'unit', e.target.value)}
                                  className="h-9"
                                />
                              </td>
                              <td className="p-2">
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={item.unit_price}
                                  onChange={(e) => handleLineItemChange(index, 'unit_price', e.target.value)}
                                  className="h-9"
                                />
                              </td>
                              <td className="p-2 text-right font-semibold">{formatMoney(item.total, businessCurrency)}</td>
                              <td className="p-2">
                                {lineItems.length > 1 && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeLineItem(index)}
                                    className="text-red-600"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Totals */}
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <div className="w-96 ml-auto space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Subtotal:</span>
                        <span className="font-semibold">{formatMoney(calculateTotals().subtotal, businessCurrency)}</span>
                      </div>
                      
                      <div className="flex justify-between items-center text-sm border-t pt-2">
                        <div className="flex items-center gap-2">
                          <Percent className="h-4 w-4 text-green-600" />
                          <span>Discount:</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Select value={discountType} onValueChange={setDiscountType}>
                            <SelectTrigger className="w-28 h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None</SelectItem>
                              <SelectItem value="percentage">Percent (%)</SelectItem>
                              <SelectItem value="fixed">Fixed ($)</SelectItem>
                            </SelectContent>
                          </Select>
                          {discountType !== 'none' && (
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={discountValue}
                              onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
                              className="w-20 h-8 text-right"
                            />
                          )}
                        </div>
                      </div>
                      {discountType !== 'none' && calculateTotals().discountAmount > 0 && (
                        <div className="flex justify-between text-sm text-green-600">
                          <span>Discount Amount:</span>
                          <span>-{formatMoney(calculateTotals().discountAmount, businessCurrency)}</span>
                        </div>
                      )}
                      
                      <div className="flex justify-between items-center text-sm">
                        <span>Tax Rate (%):</span>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={taxRate}
                          onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                          className="w-24 h-8 text-right"
                        />
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Tax Amount:</span>
                        <span>{formatMoney(calculateTotals().taxAmount, businessCurrency)}</span>
                      </div>
                      
                      <div className="flex justify-between text-lg font-bold border-t-2 pt-3 border-green-300">
                        <span>Estimated Total:</span>
                        <span className="text-green-600">{formatMoney(calculateTotals().total, businessCurrency)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-green-600 hover:bg-green-700">
                      Create Estimate
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {/* Search and Filter */}
          <div className="mb-6 flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by client, estimate #..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="estimate-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]" data-testid="estimate-status-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="declined">Declined</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[170px]" data-testid="estimate-sort-by">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="valid_until">Newest Valid-Until</SelectItem>
                <SelectItem value="created_at">Newest Created</SelectItem>
                <SelectItem value="amount">Highest Amount</SelectItem>
                <SelectItem value="status">Status</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {estimates.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No estimates yet</p>
              <p className="text-gray-400 text-sm">Create your first estimate to get started</p>
            </div>
          ) : filteredEstimates.length === 0 ? (
            <div className="text-center py-12">
              <Search className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No estimates found</p>
              <p className="text-gray-400 text-sm">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Estimate #</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Client</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Valid Until</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredEstimates.map((estimate) => (
                    <tr key={estimate.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 text-sm font-medium">{estimate.estimate_number}</td>
                      <td className="px-4 py-4 text-sm">{estimate.client_name}</td>
                      <td className="px-4 py-4 text-sm font-semibold">{formatMoney(estimate.amount || 0, businessCurrency)}</td>
                      <td className="px-4 py-4 text-sm">{estimate.valid_until}</td>
                      <td className="px-4 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(estimate.status)}`}>
                          {estimate.status?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => setViewEstimate(estimate)} title="View">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleEditEstimate(estimate)} title="Edit">
                            <Edit className="h-4 w-4" />
                          </Button>
                          {estimate.status !== 'converted' && (
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="text-green-600 hover:bg-green-50"
                              onClick={() => handleConvertToInvoice(estimate.id)} 
                              title="Convert to Invoice"
                            >
                              <ArrowRight className="h-4 w-4" />
                            </Button>
                          )}
                          <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDeleteEstimate(estimate.id)} title="Delete">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Estimate Dialog */}
      {viewEstimate && (
        <Dialog open={!!viewEstimate} onOpenChange={() => setViewEstimate(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Estimate Details</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{businessName}</h3>
                    <p className="text-gray-600">Estimate #{viewEstimate.estimate_number}</p>
                  </div>
                  <span className={`px-4 py-2 rounded-full text-sm font-semibold border ${getStatusColor(viewEstimate.status)}`}>
                    {viewEstimate.status?.toUpperCase()}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600 font-medium">Client:</p>
                    <p className="text-gray-900 font-semibold">{viewEstimate.client_name}</p>
                    {viewEstimate.client_address && (
                      <p className="text-gray-600 text-xs mt-1">{viewEstimate.client_address}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-gray-600 font-medium">Estimated Total:</p>
                    <p className="text-2xl font-bold text-green-600">{formatMoney(viewEstimate.amount || 0, businessCurrency)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 font-medium">Valid Until:</p>
                    <p className="text-gray-900">{viewEstimate.valid_until}</p>
                  </div>
                </div>
              </div>
              
              {/* Line Items Table */}
              {parseEstimateItems(viewEstimate).length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-green-100 px-4 py-2 font-semibold text-gray-700">Line Items</div>
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold">Code</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold">Description</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold">Qty</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold">Unit Price</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {parseEstimateItems(viewEstimate).map((item, index) => (
                        <tr key={index}>
                          <td className="px-4 py-2 text-sm">{item.code || '-'}</td>
                          <td className="px-4 py-2 text-sm">{item.description}</td>
                          <td className="px-4 py-2 text-sm text-right">{item.unit}</td>
                          <td className="px-4 py-2 text-sm text-right">{formatMoney(parseFloat(item.unit_price), businessCurrency)}</td>
                          <td className="px-4 py-2 text-sm font-semibold text-right">{formatMoney(parseFloat(item.total), businessCurrency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Totals */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="space-y-2 text-sm">
                  {viewEstimate.subtotal && (
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span>{formatMoney(parseFloat(viewEstimate.subtotal), businessCurrency)}</span>
                    </div>
                  )}
                  {viewEstimate.discount_amount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount:</span>
                      <span>-{formatMoney(parseFloat(viewEstimate.discount_amount), businessCurrency)}</span>
                    </div>
                  )}
                  {viewEstimate.tax_rate > 0 && (
                    <div className="flex justify-between">
                      <span>Tax ({viewEstimate.tax_rate}%):</span>
                      <span>{formatMoney(parseFloat(viewEstimate.tax_amount || 0), businessCurrency)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>Estimated Total:</span>
                    <span className="text-green-600">{formatMoney(viewEstimate.amount || 0, businessCurrency)}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { handleEditEstimate(viewEstimate); setViewEstimate(null); }} className="gap-2">
                  <Edit className="h-4 w-4" /> Edit
                </Button>
                {viewEstimate.status !== 'converted' && (
                  <Button variant="outline" className="gap-2 text-green-600 border-green-600 hover:bg-green-50" onClick={() => handleConvertToInvoice(viewEstimate.id)}>
                    <ArrowRight className="h-4 w-4" /> Convert to Invoice
                  </Button>
                )}
                <Button variant="outline" onClick={() => handleExportEstimate(viewEstimate, 'pdf')} className="gap-2">
                  <Download className="h-4 w-4" /> PDF
                </Button>
                <Button variant="outline" onClick={() => handleExportEstimate(viewEstimate, 'excel')} className="gap-2">
                  <Download className="h-4 w-4" /> Excel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Estimate Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={(open) => { setIsEditDialogOpen(open); if (!open) { setEditingEstimate(null); resetForm(); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Estimate</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateEstimate} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_client_name">Client Name *</Label>
                <Input
                  id="edit_client_name"
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit_valid_until">Valid Until *</Label>
                <Input
                  id="edit_valid_until"
                  type="date"
                  value={formData.valid_until}
                  onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                  required
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="edit_client_address">Client Address</Label>
              <Textarea
                id="edit_client_address"
                value={formData.client_address}
                onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
                rows={2}
              />
            </div>
            
            <div>
              <Label htmlFor="edit_status">Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="accepted">Accepted</SelectItem>
                  <SelectItem value="declined">Declined</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Line Items */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className="text-base font-semibold">Line Items</Label>
                <Button type="button" variant="outline" size="sm" onClick={addLineItem} className="gap-1">
                  <Plus className="h-4 w-4" /> Add Item
                </Button>
              </div>
              <div className="space-y-2">
                {lineItems.map((item, index) => (
                  <div key={index} className="grid grid-cols-6 gap-2 items-start">
                    <Input
                      placeholder="Code"
                      value={item.code}
                      onChange={(e) => handleLineItemChange(index, 'code', e.target.value)}
                      onFocus={(e) => {
                        const rect = e.target.getBoundingClientRect();
                        setDropdownPos({ top: rect.bottom + 2, left: rect.left });
                        if (productsCatalog.length > 0) {
                          setFilteredItems(productsCatalog);
                          setShowItemDropdown(index);
                        }
                      }}
                      onBlur={() => setTimeout(() => setShowItemDropdown(null), 300)}
                      autoComplete="off"
                      data-testid={`edit-estimate-line-item-code-${index}`}
                    />
                    {showItemDropdown === index && filteredItems.length > 0 && ReactDOM.createPortal(
                      <div
                        style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, zIndex: 99999, pointerEvents: 'auto' }}
                        className="w-72 bg-white border border-gray-300 rounded-md shadow-xl max-h-60 overflow-y-auto"
                      >
                        <div className="px-3 py-1 bg-blue-50 text-xs font-semibold text-blue-700 sticky top-0">
                          Products & Services
                        </div>
                        {filteredItems.map((product) => (
                          <div
                            key={product.id}
                            className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                            onMouseDown={(e) => { e.preventDefault(); handleSelectProduct(index, product); }}
                            data-testid={`edit-estimate-product-option-${product.id}`}
                          >
                            <div className="flex justify-between">
                              <span className="font-medium text-sm">{product.name}</span>
                              <span className="text-sm text-green-600">{formatMoney(product.default_price || 0, businessCurrency)}</span>
                            </div>
                            {product.description && (
                              <div className="text-xs text-gray-400 truncate">{product.description}</div>
                            )}
                          </div>
                        ))}
                      </div>,
                      document.body
                    )}
                    <Textarea placeholder="Description" className="col-span-2 min-h-[40px]" value={item.description} onChange={(e) => handleLineItemChange(index, 'description', e.target.value)} rows={1} />
                    <Input type="number" placeholder="Qty" value={item.unit} onChange={(e) => handleLineItemChange(index, 'unit', e.target.value)} />
                    <Input type="number" placeholder="Price" value={item.unit_price} onChange={(e) => handleLineItemChange(index, 'unit_price', e.target.value)} />
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{formatMoney(item.total, businessCurrency)}</span>
                      {lineItems.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeLineItem(index)} className="text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span>Subtotal:</span>
                  <span>{formatMoney(calculateTotals().subtotal, businessCurrency)}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span>Discount:</span>
                  <div className="flex items-center gap-2">
                    <Select value={discountType} onValueChange={setDiscountType}>
                      <SelectTrigger className="w-28 h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="percentage">Percent</SelectItem>
                        <SelectItem value="fixed">Fixed</SelectItem>
                      </SelectContent>
                    </Select>
                    {discountType !== 'none' && (
                      <Input type="number" value={discountValue} onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)} className="w-20 h-8" />
                    )}
                  </div>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span>Tax (%):</span>
                  <Input type="number" value={taxRate} onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)} className="w-24 h-8" />
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Estimated Total:</span>
                  <span className="text-green-600">{formatMoney(calculateTotals().total, businessCurrency)}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => { setIsEditDialogOpen(false); setEditingEstimate(null); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700">
                Update Estimate
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Estimates;
